import { useGetIntegrations } from "../../lib/hooks"
import { useEffect, useRef, useState } from "react"
import { client, validateClientProperty } from "@/lib/ts-rest"
import { Button } from "../ui/button"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import { ExternalLinkIcon, RotateCwIcon } from "lucide-react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { queryClient } from "@/lib/react-query"
import { trackEvent, captureError } from "@/lib/log"

export function ConnectAccounts({ integrationId }: { integrationId: number }) {
  const { data, refetch } = useGetIntegrations()
  const integration = data?.body.integrations.find(
    (item) => item.integration.id === Number(integrationId),
  )
  const query = useSearch({ strict: false }) as Record<string, string>
  const navigate = useNavigate()

  const hasAlexaAccount = !!integration?.integration.alexaAccount

  const [polling, setPolling] = useState(false)
  const [enabling, setEnabling] = useState(false)

  const { mutateAsync: enableSkill } = client.enableSkill.useMutation()

  const enableStarted = useRef(false)
  useEffect(() => {
    const amazonCode = query.amazonCode
    if (amazonCode && !enableStarted.current) {
      enableStarted.current = true
      setEnabling(true)

      const url = new URL(window.location.href)
      url.searchParams.delete("amazonCode")
      window.history.replaceState({}, "", url.pathname + url.search)

      trackEvent("started enabling Alexa skill", { integrationId })
      enableSkill({ body: { amazonAuthCode: amazonCode } })
        .then(() => {
          trackEvent("completed enabling Alexa skill", { integrationId })
          setPolling(true)
        })
        .catch((error) => {
          captureError(error, { tags: { source: "alexa_skill_enable" } })
          setEnabling(false)
          enableStarted.current = false
        })
    }
  }, [query.amazonCode, enableSkill])

  useEffect(() => {
    if (polling && !hasAlexaAccount) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({
          queryKey: [validateClientProperty("getIntegrations")],
        })
      }, 3000)
      return () => clearInterval(interval)
    }
    if (polling && hasAlexaAccount) {
      setPolling(false)
      setEnabling(false)
    }
  }, [polling, hasAlexaAccount])

  const { data: skillLinkingUrl } =
    client.getAmazonAuthorizeUrlForSkillLinking.useQuery({
      queryKey: [
        validateClientProperty("getAmazonAuthorizeUrlForSkillLinking"),
        String(integrationId),
      ],
      queryData: {
        query: { integrationId: String(integrationId) },
      },
      enabled: !enabling,
    })

  function handleLoginWithAmazon() {
    if (skillLinkingUrl?.body.url) {
      trackEvent("clicked log in with Amazon", { integrationId })
      window.open(skillLinkingUrl.body.url, "_self")
    }
  }

  if (hasAlexaAccount) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 w-fit">
          <CheckBadgeIcon className="h-6 w-6 shrink-0" />
          <span className="font-medium">Alexa connected</span>
        </div>
        <Button
          variant="outline"
          className="w-fit"
          onClick={handleLoginWithAmazon}
          disabled={!skillLinkingUrl?.body.url}
        >
          Change Amazon account
          <ExternalLinkIcon className="ml-2 w-4 h-4" />
        </Button>
      </div>
    )
  }

  if (enabling) {
    return (
      <div className="flex items-center gap-3">
        <RotateCwIcon className="w-5 h-5 text-primary animate-spin" />
        <span>Connecting your Alexa account...</span>
      </div>
    )
  }

  return (
    <Button
      className="w-fit"
      onClick={handleLoginWithAmazon}
      disabled={!skillLinkingUrl?.body.url}
    >
      Log in with Amazon
      <ExternalLinkIcon className="ml-2 w-4 h-4" />
    </Button>
  )
}
